import { useEffect, useState, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { api } from "../lib/api";
import { Bell, UserCheck, AlertCircle, UserPlus } from "lucide-react";

export function NotificationBell({ onNavigate, user }) {
  const isAdmin = user?.role === "admin";
  const [items, setItems] = useState([]);
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const load = () => {
    if (isAdmin) {
      api.get("/auth/users").then((r) => setItems((r.data || []).filter((u) => u.role === "pending"))).catch(() => {});
    } else {
      api.get("/today").then((r) => setItems(r.data.to_contact || [])).catch(() => {});
    }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 60000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [isAdmin]);

  useEffect(() => {
    const onClick = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const count = items.length;

  return (
    <div className="relative" ref={ref}>
      <button data-testid="notification-bell" onClick={() => setOpen((o) => !o)}
        className="relative h-10 w-10 grid place-items-center rounded-xl bg-white/5 border border-white/10 hover:bg-white/10 transition-colors">
        <Bell className="h-5 w-5 text-slate-300" />
        {count > 0 && (
          <span data-testid="notification-count"
            className="absolute -top-1.5 -right-1.5 min-w-[20px] h-5 px-1 grid place-items-center rounded-full bg-rose-500 text-white text-[11px] font-bold">
            {count}
          </span>
        )}
      </button>

      <AnimatePresence>
        {open && (
          <motion.div initial={{ opacity: 0, y: -8, scale: 0.98 }} animate={{ opacity: 1, y: 0, scale: 1 }} exit={{ opacity: 0, y: -8, scale: 0.98 }}
            className="absolute right-0 mt-2 w-80 max-w-[calc(100vw-2rem)] glass rounded-2xl border border-white/10 shadow-2xl p-3 z-50"
            data-testid="notification-panel">
            <div className="flex items-center gap-2 px-1 pb-2 mb-1 border-b border-white/10">
              {isAdmin ? <UserPlus className="h-4 w-4 text-amber-400" /> : <UserCheck className="h-4 w-4 text-emerald-400" />}
              <p className="font-display font-bold text-sm">
                {isAdmin ? "Nuovi iscritti da approvare" : "Clienti da ricontattare oggi"}
              </p>
            </div>

            {count === 0 ? (
              <p className="text-sm text-slate-400 px-1 py-4 text-center">
                {isAdmin ? "Nessuna nuova registrazione in attesa. ✨" : "Tutto in regola! Nessun check-in in sospeso. 🎉"}
              </p>
            ) : isAdmin ? (
              <div className="space-y-1.5 max-h-72 overflow-y-auto">
                {items.map((u) => (
                  <button key={u.id} data-testid={`notif-user-${u.id}`} onClick={() => { setOpen(false); onNavigate("users"); }}
                    className="w-full text-left p-2.5 rounded-xl bg-white/[0.03] hover:bg-white/[0.07] border border-white/5 transition-colors">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-sm truncate">{u.name}</span>
                      <span className="text-[11px] text-amber-300 shrink-0">in attesa</span>
                    </div>
                    <p className="text-[11px] text-slate-400 mt-0.5 truncate">{u.email}</p>
                  </button>
                ))}
              </div>
            ) : (
              <div className="space-y-1.5 max-h-72 overflow-y-auto">
                {items.map((c) => {
                  const worst = Math.max(...c.pending.map((p) => p.overdue_days));
                  return (
                    <button key={c.id} data-testid={`notif-client-${c.id}`} onClick={() => { setOpen(false); onNavigate("wellness"); }}
                      className="w-full text-left p-2.5 rounded-xl bg-white/[0.03] hover:bg-white/[0.07] border border-white/5 transition-colors">
                      <div className="flex items-center justify-between gap-2">
                        <span className="font-semibold text-sm truncate">{c.name}</span>
                        <span className="text-[11px] text-rose-300 flex items-center gap-1 shrink-0">
                          <AlertCircle className="h-3 w-3" />
                          {worst === 0 ? "oggi" : `+${worst}g`}
                        </span>
                      </div>
                      <p className="text-[11px] text-slate-400 mt-0.5">
                        Check-in giorno {c.pending.map((p) => p.day).join(", ")}
                        {c.program ? ` · ${c.program}` : ""}
                      </p>
                    </button>
                  );
                })}
              </div>
            )}

            <button onClick={() => { setOpen(false); onNavigate(isAdmin ? "users" : "wellness"); }}
              className="w-full mt-2 text-xs text-emerald-300 hover:text-emerald-200 py-2 rounded-lg hover:bg-emerald-500/10 transition-colors font-medium">
              {isAdmin ? "Vai a Gestione Utenti →" : "Vai a Clienti & Assistenza →"}
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}
